import "./HeroTurbine.css";

import { createTimeline, stagger } from "animejs";
import { ArrowRight } from "lucide-react";
import { type CSSProperties, type ReactNode,useEffect, useRef } from "react";

import { prefersReducedMotion,step } from "@/4-components/animations/anime";
import { EASE } from "@/5-shared/constants/motion";
import { Link } from "@/5-shared/routing";

import type { HeroScene } from "./heroScene";
import { hasHardwareGpu } from "./heroScene/gpu";

const SCROLL_KEYS = new Set([" ", "PageDown", "PageUp", "ArrowDown", "ArrowUp", "Home", "End"]);

const IMPATIENCE = 2800;
const GRACE = 1500;
const GAP = 220;

/**
 * The whole entrance choreography, tunable in one place. `at` is the beat's
 * start offset on the timeline, `dur` its length (ms); easings come from the
 * motion tokens. Nothing else in this file hard-codes a duration or ease.
 */
const ENTRANCE = {
  iconScale: 2.4, // the card icon starts this big, then settles to 1
  defaultEase: EASE.inOutSoft,
  cam1: { at: 0, dur: 850 },
  array: { at: 750, dur: 700, ease: EASE.inOut },
  tower: { at: 1150, dur: 850, ease: EASE.out },
  camEnd: { at: 1250, dur: 1250, ease: EASE.inOut },
  nacelle: { at: 1900, dur: 340, ease: EASE.out },
  blades: { at: 2050, dur: 460, stagger: 130, ease: EASE.out },
  spin: { at: 2350, dur: 900, ease: EASE.outSoft },
  extras: { at: 2250, dur: 540, stagger: 180, ease: EASE.out },
  cow: { at: 2450, dur: 620, ease: EASE.out },
  card: { at: 2900, dur: 440, ease: EASE.out },
  icon: { at: 3440, dur: 720, ease: EASE.inOut },
  reveal: { at: 4260, dur: 480, linksDur: 520, ease: EASE.out },
  say: { at: 4460, dur: 250 },
} as const;

type HeroTurbineContent = {
  eyebrow: string;
  title: ReactNode;
  subtitle: string;
  cta: { label: string; to: string };

  card: { icon: ReactNode; head: string; sub: string[] };
  cowLine: string;
  className?: string;
};

/** Runs a self-timed WebGL hero; reduced motion and scene failures keep the static copy visible, while Escape or sustained input releases the scroll hold. */
export function HeroTurbine({ eyebrow, title, subtitle, cta, card, cowLine, className = "" }: HeroTurbineContent) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const linksRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const host = hostRef.current;
    const copy = copyRef.current;
    const card = cardRef.current;
    const surface = surfaceRef.current;
    const icon = iconRef.current;
    const text = textRef.current;
    const links = linksRef.current;
    if (!section || !host || !copy || !card || !surface || !icon || !text || !links) return;

    let scene: HeroScene | undefined;
    let stop = () => {};
    let cancelled = false;

    /* "DEC World" planet is the default hero; the original flat field stays
       reachable for comparison via ?hero=field (or hero=field in localStorage). */
    const variant: "field" | "planet" =
      new URLSearchParams(window.location.search).get("hero") === "field" ||
      window.localStorage.getItem("hero") === "field"
        ? "field"
        : "planet";
    if (variant === "planet") section.classList.add("ht-hero--planet");
    /* Company reads as a warm sun, producer (the blue skin) as a cool sky. */
    const sky: "sun" | "sky" = section.classList.contains("hero-turbine--blue") ? "sky" : "sun";

    /* The headline is painted from the prerendered HTML (see .ht-copy) and stays
       up throughout, it's the LCP element, so it is never hidden behind the
       fonts wait, the three.js import or the entrance timeline. revealCopy is
       kept as a defensive no-op the bail paths can still call. */
    const reduce = prefersReducedMotion();
    const revealCopy = () => {
      copy.style.opacity = "1";
    };

    /* Without a hardware GPU (headless/CI, GPU-less devices) this scene renders
       on the CPU at ~100ms/frame and would pin the main thread. Keep the
       prerendered static hero for those clients, and skip loading three.js. */
    if (!hasHardwareGpu()) {
      revealCopy();
      return;
    }

    void (async () => {
      await document.fonts?.ready;

      try {
        const { buildHeroScene } = await import("./heroScene");
        if (cancelled) return;
        scene = await buildHeroScene(host, { variant, sky });
      } catch (err) {
        console.warn("HeroTurbine: scene unavailable, keeping the static hero.", err);
        revealCopy();
        return;
      }
      if (cancelled) {
        scene.dispose();
        return;
      }
      const p = scene.params;
      host.style.opacity = "1";

      const css = getComputedStyle(section);
      const endX = parseFloat(css.getPropertyValue("--ht-cam-x")) || 0;
      const endY = parseFloat(css.getPropertyValue("--ht-cam-y")) || 6;
      const endDist = parseFloat(css.getPropertyValue("--ht-cam-dist")) || 30;

      if (reduce) {
        scene.freeze();
        revealCopy();
        return;
      }

      const BIG = ENTRANCE.iconScale;
      icon.style.transformOrigin = "center";
      icon.style.transform = "none";
      const cardBox = card.getBoundingClientRect();
      const iconBox = icon.getBoundingClientRect();
      const dx = cardBox.left + cardBox.width / 2 - (iconBox.left + iconBox.width / 2);
      const dy = cardBox.top + cardBox.height / 2 - (iconBox.top + iconBox.height / 2);
      icon.style.transform = `translate(${dx}px, ${dy}px) scale(${BIG})`;

      const tl = createTimeline({ autoplay: false, defaults: { ease: ENTRANCE.defaultEase }, onComplete: () => release() });

      tl.add(p, { camDist: [5.4, 14], camAz: [0.2, 0.62], camEl: [0.5, 0.3], camY: [1, 2.1], duration: ENTRANCE.cam1.dur }, ENTRANCE.cam1.at)
        .add(p, { arrayScale: 1.2, arrayX: -6.4, arrayZ: 1.8, duration: ENTRANCE.array.dur, ease: ENTRANCE.array.ease }, ENTRANCE.array.at)
        .add(p, { tower: [0, 1], duration: ENTRANCE.tower.dur, ease: ENTRANCE.tower.ease }, ENTRANCE.tower.at)
        .add(p, { camDist: endDist, camAz: 0.95, camEl: 0.22, camY: endY, camX: endX, duration: ENTRANCE.camEnd.dur, ease: ENTRANCE.camEnd.ease }, ENTRANCE.camEnd.at)
        .add(p, { nacelle: [0, 1], duration: ENTRANCE.nacelle.dur, ease: ENTRANCE.nacelle.ease }, ENTRANCE.nacelle.at)
        .add(p.blades, { out: 1, duration: ENTRANCE.blades.dur, delay: stagger(ENTRANCE.blades.stagger), ease: ENTRANCE.blades.ease }, ENTRANCE.blades.at)
        .add(p, { spinRate: 0.85, duration: ENTRANCE.spin.dur, ease: ENTRANCE.spin.ease }, ENTRANCE.spin.at)
        .add(p.extras, { on: 1, duration: ENTRANCE.extras.dur, delay: stagger(ENTRANCE.extras.stagger), ease: ENTRANCE.extras.ease }, ENTRANCE.extras.at)
        .add(p, { cow: [0, 1], duration: ENTRANCE.cow.dur, ease: ENTRANCE.cow.ease }, ENTRANCE.cow.at)
        .add(card, { opacity: [0, 1], duration: ENTRANCE.card.dur, ease: ENTRANCE.card.ease }, ENTRANCE.card.at)
        .add(icon, { translateX: [dx, 0], translateY: [dy, 0], scale: [BIG, 1], duration: ENTRANCE.icon.dur, ease: ENTRANCE.icon.ease }, ENTRANCE.icon.at)
        .add(surface, { opacity: [0, 1], duration: ENTRANCE.reveal.dur, ease: ENTRANCE.reveal.ease }, ENTRANCE.reveal.at)
        .add(text, { opacity: [0, 1], duration: ENTRANCE.reveal.dur, ease: ENTRANCE.reveal.ease }, ENTRANCE.reveal.at)
        .add(links, { opacity: [0, 1], duration: ENTRANCE.reveal.linksDur, ease: ENTRANCE.reveal.ease }, ENTRANCE.reveal.at)
        .add(p, { say: [0, 1], duration: ENTRANCE.say.dur }, ENTRANCE.say.at);
      let held = false;
      let done = false;
      let anchor = 0;
      let pushed = 0;
      let touchY = 0;
      let failsafe = 0;
      let startedAt = 0;
      let lastPush = 0;

      function release() {
        if (done) return;
        done = true;
        held = false;
        window.clearTimeout(failsafe);
        detach();
        // the entrance is over: let the scene drop to its cheaper ~32fps idle
        // for the still-running rotor/cow
        scene?.settle();
        revealCopy(); // whatever path ended the entrance, the headline stays up
      }

      function skip() {
        tl.seek(tl.duration);
        release();
      }

      const begin = (e?: Event) => {
        const r = section.getBoundingClientRect();
        if (r.bottom < window.innerHeight * 0.5) {
          // Loaded already scrolled past the hero (scroll restoration, back-nav):
          // seek to the finished frame instead of releasing at the start one, so
          // the turbine shows built, not half-assembled.
          skip();
          return;
        }
        e?.preventDefault();
        held = true;
        anchor = window.scrollY;
        pushed = 0;
        startedAt = performance.now();
        tl.play();
        failsafe = window.setTimeout(release, tl.duration + 900);
      };

      const shove = (amount: number) => {
        const now = performance.now();
        if (now - startedAt < GRACE) return;
        if (now - lastPush > GAP) pushed = 0;
        lastPush = now;
        pushed += amount;
        if (pushed > IMPATIENCE) skip();
      };

      const onWheel = (e: WheelEvent) => {
        if (done) return;
        if (!held) return begin(e);
        e.preventDefault();
        shove(Math.abs(e.deltaY));
      };

      const onTouchStart = (e: TouchEvent) => {
        touchY = e.touches[0]?.clientY ?? 0;
        if (!done && !held) return;
      };

      const onTouchMove = (e: TouchEvent) => {
        if (done) return;
        if (!held) return begin(e);
        e.preventDefault();
        const y = e.touches[0]?.clientY ?? touchY;
        shove(Math.abs(y - touchY));
        touchY = y;
      };

      const onKey = (e: KeyboardEvent) => {
        if (done) return;
        if (e.key === "Escape" && held) return skip();
        if (!SCROLL_KEYS.has(e.key)) return;
        if (!held) return begin(e);
        e.preventDefault();
      };

      const onScroll = () => {
        if (held) window.scrollTo(0, anchor);
      };

      function detach() {
        window.removeEventListener("wheel", onWheel);
        window.removeEventListener("touchstart", onTouchStart);
        window.removeEventListener("touchmove", onTouchMove);
        window.removeEventListener("keydown", onKey);
        window.removeEventListener("scroll", onScroll);
      }

      window.addEventListener("wheel", onWheel, { passive: false });
      window.addEventListener("touchstart", onTouchStart, { passive: true });
      window.addEventListener("touchmove", onTouchMove, { passive: false });
      window.addEventListener("keydown", onKey);
      window.addEventListener("scroll", onScroll, { passive: true });

      begin();

      stop = () => {
        window.clearTimeout(failsafe);
        detach();
        tl.revert();
      };
    })();

    return () => {
      cancelled = true;
      stop();
      scene?.dispose();
    };
  }, []);

  return (
    <div ref={sectionRef} className={`ht-hero relative bg-zinc-50 border-b border-zinc-200 ${className}`}>
      {/* Without JS there is no entrance to reveal the CSS-hidden copy, so show it. */}
      <noscript>
        <style>{`.ht-copy{opacity:1}`}</style>
      </noscript>
      <div className="ht-stage sticky top-0 h-svh overflow-hidden flex items-center justify-center">
        <div ref={hostRef} className="ht-canvas absolute inset-0" aria-hidden="true">
          <div className="ht-contract" style={{ "--ht-x": "var(--ht-a0-x)", "--ht-y": "var(--ht-a0-y)", "--ht-on": "var(--ht-a0-on)" } as CSSProperties}>
            <div ref={linksRef} className="ht-links">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="ht-link"
                  style={{ "--len": `var(--ht-l${i}-len)`, "--rot": `var(--ht-l${i}-rot)`, "--on": `var(--ht-l${i}-on)` } as CSSProperties}
                />
              ))}
            </div>
            <div ref={cardRef} className="ht-contract-card">
              <div ref={surfaceRef} className="ht-contract-surface" />
              <div ref={iconRef} className="ht-contract-doc">
                {card.icon}
              </div>
              <div ref={textRef} className="ht-contract-text">
                <div className="ht-contract-head">{card.head}</div>
                <ul className="ht-contract-sub">
                  {card.sub.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="ht-say" style={{ "--ht-x": "var(--ht-a3-x)", "--ht-y": "var(--ht-a3-y)", "--ht-on": "var(--ht-a3-on)" } as CSSProperties}>
            {cowLine}
          </div>
        </div>

        <div ref={copyRef} className="ht-copy shell relative z-10 text-center lg:text-left">
          <div className="lg:max-w-xl">
            <span data-h style={step(0)} className="inline-block mb-6 eyebrow">
              {eyebrow}
            </span>
            <h1 data-h style={step(1)} className="hero-title">
              {title}
            </h1>
            <p data-h style={step(2)} className="hero-lede mx-auto mt-6 lg:mx-0">
              {subtitle}
            </p>

            <div data-h style={step(4)} className="mt-10 hidden md:flex flex-wrap items-center justify-center lg:justify-start gap-4">
              <Link to={cta.to} className="group inline-flex items-center gap-2 btn btn-lg btn-primary shadow-sm">
                {cta.label}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
